import { useParams, Link, Navigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { SERVICES, GALLERY, BLOG_POSTS } from '@/data/content';
import { SectionHeading, PortfolioImage } from '@/components/site/SiteBits';
import { Seo, Breadcrumbs, BUSINESS_ID, SITE_URL } from '@/components/site/Seo';
import FinalCTA from '@/components/site/FinalCTA';

// Map each service slug to the gallery item IDs that best illustrate it.
const SERVICE_TO_GALLERY = {
  'door-installation-repair': ['wood-double-doors', 'white-double-doors', 'black-door-red-trim'],
  'interior-painting': ['interior-trim', 'dresser-refinish', 'door-prep-paint'],
  'exterior-painting': ['commercial-diamond-wall', 'door-prep-paint', 'black-door-red-trim'],
  'drywall-texture-repairs': ['drywall-texture-project', 'interior-trim', 'home-gym-build'],
  'trim-carpentry': ['interior-trim', 'home-gym-build'],
  'small-construction-repair': ['lattice-patio', 'fence-gate-close', 'fence-gate-collage', 'home-gym-build'],
  'permanent-lighting': ['permanent-lighting-roofline'],
  'flooring': ['flooring-subfloor-rebuild'],
};

// Related reading for each service — internal links to relevant blog articles.
const SERVICE_TO_BLOG = {
  'door-installation-repair': ['replacing-exterior-door'],
  'interior-painting': ['good-painting-estimate', 'cheapest-contractor-costs-more'],
  'exterior-painting': ['signs-home-needs-exterior-paint', 'good-painting-estimate'],
  'drywall-texture-repairs': ['drywall-texture-matching-skill'],
  'trim-carpentry': ['trim-carpentry-cost-guide', 'cheapest-contractor-costs-more'],
  'small-construction-repair': ['prepare-small-home-repair', 'cheapest-contractor-costs-more'],
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  if (!service) return <Navigate to="/services" replace />;

  const Icon = Icons[service.icon] || Icons.Hammer;

  const path = `/services/${service.slug}`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: service.title,
    name: service.title,
    provider: { '@id': BUSINESS_ID },
    areaServed: ['Fort Worth, TX', 'Burleson, TX', 'Weatherford, TX', 'Granbury, TX', 'Cleburne, TX'],
    url: `${SITE_URL}${path}`,
    description: service.seoDescription,
  };

  // Pick gallery items relevant to this specific service
  const relatedIds = SERVICE_TO_GALLERY[slug] || [];
  const related = relatedIds
    .map((id) => GALLERY.find((g) => g.id === id))
    .filter(Boolean)
    .slice(0, 3);
  const otherServices = SERVICES.filter((s) => s.slug !== slug).slice(0, 4);

  return (
    <div data-testid={`page-service-${slug}`}>
      <Seo title={service.seoTitle} description={service.seoDescription} path={path} jsonLd={jsonLd} />
      <section className="border-b border-brand-border bg-brand-bone">
        <div className="container-x py-14 lg:py-20">
          <Breadcrumbs
            items={[{ label: 'Home', to: '/' }, { label: 'Services', to: '/services' }, { label: service.title }]}
            testId={`breadcrumbs-${slug}`}
          />
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12 items-start">
          <div>
            <Icon size={30} strokeWidth={1.5} className="text-brand-maroon mt-2" />
            <h1 className="h1 mt-4">{service.title}</h1>
            <p className="body-lg mt-6 max-w-xl">{service.intro}</p>
            <div className="mt-8 flex gap-3 flex-col sm:flex-row">
              <Link to="/estimate" className="btn-primary" data-testid={`service-cta-${slug}`}>Request an Estimate</Link>
              <Link to="/gallery" className="btn-secondary">See related work</Link>
            </div>
          </div>
          <div>
          {service.image && (
            <div className="crop-frame w-full aspect-[16/10] border border-brand-border mb-6">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover"
                loading="eager"
                data-testid={`service-hero-img-${slug}`}
              />
            </div>
          )}
          <div className="border border-brand-border bg-brand-bone p-8">
            {slug === 'permanent-lighting' && (
              <img
                src="/brand/shawtech-square-light.webp"
                alt="Installation Partner of ShawTech Lighting"
                className="w-40 h-auto mb-6"
                loading="lazy"
                data-testid="service-shawtech-badge"
              />
            )}
            <div className="eyebrow mb-3">What's included</div>
            <ul className="space-y-2.5 font-sans text-sm text-brand-charcoal">
              {service.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5">
                  <Icons.Check size={16} className="mt-0.5 text-brand-maroon flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-5 border-t border-brand-border">
              <div className="eyebrow mb-2">Our focus</div>
              <p className="font-serif text-brand-charcoal/85 text-base leading-relaxed">{service.focus}</p>
            </div>
          </div>
          </div>
          </div>
        </div>
      </section>

      {service.approach && (
        <section className="border-b border-brand-border">
          <div className="container-x py-16 lg:py-24 max-w-3xl">
            <div className="eyebrow mb-3">Our approach</div>
            <h2 className="h2">How we handle {service.title.toLowerCase()}.</h2>
            <div className="mt-8 space-y-5 font-sans text-base lg:text-[17px] leading-[1.85] text-brand-charcoal/90">
              {service.approach.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="surface-paper border-b border-brand-border">
        <div className="container-x py-16 lg:py-24">
          <SectionHeading eyebrow="Recent work" title="A look at similar jobs" />
          {related.length > 0 ? (
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((g) => (
                <Link key={g.id} to={`/gallery/${g.id}`} className="group block border border-brand-border bg-brand-bone hover:border-brand-charcoal transition-colors">
                  <PortfolioImage src={g.cover || g.src} className="aspect-[4/3]" alt={g.title} />
                  <div className="p-4">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-brand-maroon">{g.category}</div>
                    <div className="mt-1 font-serif text-base text-brand-charcoal group-hover:text-brand-maroon transition-colors">{g.title}</div>
                    <p className="mt-1 font-sans text-xs text-brand-muted leading-relaxed">{g.caption}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="mt-10 border border-dashed border-brand-border bg-brand-bone p-12 text-center">
              <div className="font-serif text-2xl text-brand-charcoal">Real project photos coming soon</div>
              <p className="mt-2 font-sans text-sm text-brand-muted">Portfolio photos for this service will appear here as new jobs wrap up.</p>
            </div>
          )}
        </div>
      </section>

      {service.faq && (
        <section className="border-b border-brand-border bg-brand-bone">
          <div className="container-x py-16 lg:py-24 max-w-3xl">
            <div className="eyebrow mb-3">Common questions</div>
            <h2 className="h2">{service.title} FAQs</h2>
            <div className="mt-10 border-t border-brand-border" data-testid={`faq-${slug}`}>
              {service.faq.map((f, i) => (
                <div key={i} className="py-6 border-b border-brand-border">
                  <h3 className="font-serif text-xl text-brand-charcoal">{f.q}</h3>
                  <p className="mt-2 font-sans text-base leading-relaxed text-brand-muted">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {(SERVICE_TO_BLOG[slug] || []).length > 0 && (
        <section className="surface-paper border-b border-brand-border">
          <div className="container-x py-16 lg:py-24">
            <SectionHeading eyebrow="From the blog" title="Related reading" />
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
              {(SERVICE_TO_BLOG[slug] || []).map((bslug) => {
                const post = (BLOG_POSTS || []).find((p) => p.slug === bslug);
                if (!post) return null;
                return (
                  <Link key={bslug} to={`/blog/${bslug}`} className="group p-6 border border-brand-border bg-brand-bone hover:bg-brand-boneAlt transition-colors" data-testid={`service-blog-${bslug}`}>
                    <div className="font-sans text-xs uppercase tracking-[0.22em] text-brand-muted">{post.date} · {post.read} read</div>
                    <div className="mt-3 font-serif text-lg text-brand-charcoal group-hover:text-brand-maroon transition-colors">{post.title}</div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {slug === 'permanent-lighting' && (
        <section className="surface-dark border-b border-brand-border text-brand-bone" data-testid="seasonal-teaser">
          <div className="container-x py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-10 items-center">
            <div className="crop-frame border border-brand-borderDark aspect-[16/10]">
              <img
                src="/portfolio/shawtech-christmas-redwhite.webp"
                alt="ShawTech permanent LED lighting in red and white Christmas colors along a home's roofline"
                className="w-full h-full object-cover"
                loading="lazy"
                data-testid="seasonal-teaser-img"
              />
            </div>
            <div>
              <div className="eyebrow mb-3 text-brand-bone/60">Holiday &amp; seasonal</div>
              <h2 className="h2 text-brand-bone">Planning holiday lighting this year?</h2>
              <p className="mt-4 font-sans text-base text-brand-bone/80 leading-relaxed max-w-xl">
                Permanent lighting earns its keep in the holiday season — and install slots fill up early across DFW. One install covers Christmas, Halloween, game days, and every night in between.
              </p>
              <Link to="/seasonal-lighting" className="btn-primary mt-8 inline-block" data-testid="seasonal-teaser-cta">
                Holiday &amp; Seasonal Lighting
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="border-b border-brand-border">
        <div className="container-x py-16 lg:py-24">
          <SectionHeading eyebrow="Explore" title="Other services" />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {otherServices.map((s) => {
              const OtherIcon = Icons[s.icon] || Icons.Hammer;
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} className="p-6 border border-brand-border bg-brand-bone hover:bg-brand-boneAlt transition-colors">
                  <OtherIcon size={20} className="text-brand-maroon" strokeWidth={1.5} />
                  <div className="mt-4 font-serif text-lg text-brand-charcoal">{s.title}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
